import mixpanel from '@disco/api/mixpanel'
import { TRACK_NESTING } from '@disco/constants/FeatureTypes'
import { CUSTOM_CHANNEL, INBOX_TYPES } from '@disco/constants/GroupTypes'
import { FILE, GROUP, PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import { makeIsFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getGroups,
  getUserSortedChannels,
  makeGetGroupLocks,
} from '@disco/state-management/selectors/groups'
import { getAllSelectedItems } from '@disco/state-management/selectors/selection'
import { getChannelsCustomOrder } from '@disco/state-management/selectors/user'
import {
  sendPlaylistDownloadEventThrottled,
  sendTrackDownloadEventThrottled,
} from '@disco/utils/eventsApi'
import { getFilesFromDraggedItem } from '@disco/utils/file-utils'
import { getFolderChannel } from '@disco/utils/groups'
import moveFromTo from '@disco/utils/moveFromTo'
import { isReadOnlyPlaylist } from '@disco/utils/playlists'
import { isDraggedItemPartOfSelection } from '@disco/utils/selection'
import { isAggregatedSearchTrack, isReadOnlyTrack } from '@disco/utils/tracks'
import cn from 'clsx'
import React, { useEffect, useState } from 'react'
import { DropTarget } from 'react-dnd'
import ReactDOM from 'react-dom'
import { connect as reduxConnect } from 'react-redux'
import type { Group, GroupLockSettings } from 'types/group'
import * as ExplorerActions from '@disco/state-management/actions/ExplorerActions'
import { updateGroup } from '@disco/state-management/actions/GroupActions'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import { uploadFilesToGroups } from '@disco/state-management/actions/upload-actions'
import { saveUserSortedChannels } from '@disco/state-management/actions/UserActions'
import { DropFilesIndicator } from 'components/ui/drop-indicator'
import useTimeout from 'hooks/use-timeout'
import { getMixpanelDragAndDropProps } from '@disco/utils/mixpanel/drop-tracks'

const getGroupsToRemove = (
  groups: Group[],
  sourceGroup: Group | null | undefined,
) => {
  const parentChannel = sourceGroup
    ? getFolderChannel(groups, sourceGroup)
    : null
  // remove groups if source group is channel
  return sourceGroup &&
    sourceGroup.group_type === CUSTOM_CHANNEL &&
    !parentChannel?.lock_content
    ? [sourceGroup]
    : []
}

const getTargetGroups = (sourceGroup, draggedInGroup) => [draggedInGroup]

const getSourceGroup = (groups, draggedItem) => {
  if (draggedItem.sourceGroupId) {
    return groups.find(g => g.id === draggedItem.sourceGroupId)
  }

  return null
}

const target = {
  canDrop(props, monitor) {
    const item = monitor.getItem()
    const { group } = props
    if (group?.permissions) {
      if (!group.permissions.content && !group.permissions.share_link)
        return false
    }
    // tie enabling file uploads in channels to nesting feature
    if (!props.nestingEnabled && item.files) return false
    const { lock_structure, lock_content } = props
    if (lock_structure && item?.group) return false
    if (lock_content && (item.track || item.playlist || item.files))
      return false
    if (item.track) return !!item.track.uploaded
    const isDraggedChannel = !item?.group?.parent
    const willDropOnFolder = group?.parent
    if (item.group && isDraggedChannel && willDropOnFolder) return false
    return true
  },

  drop(props, monitor) {
    const draggedItem = monitor.getItem()
    const hasDroppedOnChild = monitor.didDrop()
    if (!draggedItem || hasDroppedOnChild) return
    const readOnlyTrack = isReadOnlyTrack(draggedItem.track)
    const readOnlyPlaylist = isReadOnlyPlaylist(draggedItem.playlist)
    const { group, groups, selectedItems } = props
    const partOfSelection = isDraggedItemPartOfSelection(
      selectedItems,
      draggedItem,
    )
    const sourceGroup = getSourceGroup(groups, draggedItem)
    const targetParent = getFolderChannel(groups, group)
    const sourceParent = getFolderChannel(groups, sourceGroup)
    const removeGroups =
      targetParent?.is_auto_tagged && targetParent?.id !== sourceParent?.id
        ? []
        : getGroupsToRemove(groups, sourceGroup)
    const targetGroups = getTargetGroups(sourceGroup, group)

    ;(async () => {
      if (readOnlyTrack) {
        const includeNestedTracks = !!sourceGroup
        const importedTrack = await props.addAggregatedTrackToDisco({
          track: draggedItem.track,
          sourceGroup: sourceGroup?.id,
          includeNestedTracks: includeNestedTracks,
          includeStems: includeNestedTracks,
        })
        draggedItem.track = importedTrack
      }

      if (readOnlyPlaylist) {
        await props.copyPlaylistToChannel(
          draggedItem.playlist,
          targetGroups[0],
          sourceGroup?.id,
        )
        props.resetSelection()
        return
      }

      // do nothing if user drags item to the same group
      if (sourceGroup?.id === group.id) return

      if (selectedItems.length > 0 && partOfSelection) {
        const playlists = selectedItems.filter(item => item.type === PLAYLIST)

        if (playlists.length > 0) {
          props.alterPlaylistGroups(playlists, targetGroups, removeGroups)
        }

        const tracks = selectedItems.filter(
          item => item.type === TRACK && !item.outside_your_territories,
        )

        if (tracks.length > 0) {
          props.alterTrackGroups(tracks, targetGroups, removeGroups)
        }

        selectedItems.forEach(item => {
          const fromInbox = INBOX_TYPES.includes(draggedItem.sourceGroupType)

          if (fromInbox && item.type === PLAYLIST) {
            playlists.forEach(playlist => {
              sendPlaylistDownloadEventThrottled(
                playlist.playlist_id,
                undefined,
                draggedItem.sourceGroupId,
              )
            })
          } else if (
            fromInbox &&
            item.type === TRACK &&
            !item.outside_your_territories
          ) {
            sendTrackDownloadEventThrottled(item.id, 'in-app', {
              group_id: draggedItem.sourceGroupId,
              playlist_id: draggedItem.sourcePlaylistId,
            })
          }
        })
      } else if (
        draggedItem.track &&
        draggedItem.track.uploaded &&
        !draggedItem.track.outside_your_territories
      ) {
        props.alterTrackGroups([draggedItem.track], targetGroups, removeGroups)

        if (INBOX_TYPES.includes(draggedItem.sourceGroupType)) {
          sendTrackDownloadEventThrottled(draggedItem.track.id, 'in-app', {
            group_id: draggedItem.sourceGroupId,
            playlist_id: draggedItem.sourcePlaylistId,
          })
        }
      } else if (draggedItem.playlist) {
        props.alterPlaylistGroups(
          [draggedItem.playlist],
          targetGroups,
          removeGroups,
        )

        if (INBOX_TYPES.includes(draggedItem.sourceGroupType)) {
          sendPlaylistDownloadEventThrottled(
            draggedItem.playlist.playlist_id,
            undefined,
            draggedItem.sourceGroupId,
          )
        }
      } else if (draggedItem.items) {
        getFilesFromDraggedItem(draggedItem).then((files: File[]) => {
          // TODO: Remove this check and handle file limits at a higher level (DISCO-7609)
          const validFiles = files.filter(f => f.size > 0)

          if (validFiles.length > 0 && targetGroups.length > 0) {
            props.uploadFilesToGroups(validFiles, targetGroups)
          }
        })
        return
      }

      if (props.enableGroupDrop && draggedItem.group) {
        // we are dragging channels, reorder them
        if (!draggedItem.group.parent && draggedItem.group.id !== group.id) {
          const sourceIndex = props.userSortedChannels.findIndex(
            g => g.id === draggedItem.group.id,
          )
          const targetIndex = props.userSortedChannels.findIndex(
            g => g.id === group.id,
          )
          const result = moveFromTo(
            props.userSortedChannels,
            sourceIndex,
            targetIndex,
          )
          props.saveUserSortedChannels(result)
          return
        }

        // we are dragging folders, move dragged item to channel
        if (draggedItem.group.parent === group.parent) return
        props.updateGroup(
          {
            ...draggedItem.group,
            image: draggedItem.image === null ? undefined : draggedItem.image,
            parent: group.id,
          },
          true,
        )
      }

      props.resetSelection()

      const eventName = draggedItem?.isPartOfMultipleSelection
        ? 'Drag and drop selected items'
        : 'Drag and drop existing track'

      mixpanel.sendPureEvent(
        eventName,
        getMixpanelDragAndDropProps(draggedItem, 'Channel'),
      )
    })()
  },
}

function collect(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver(),
    draggedItem: monitor.getItem(),
    canDrop: monitor.canDrop(),
  }
}

type Props = GroupLockSettings & {
  isOver: boolean
  group: Group
  enableGroupDrop?: boolean | null | undefined
  children: React.ReactNode
  draggedItem: Record<string, any> | null | undefined
  connectDropTarget: (...args: Array<any>) => any
  canDrop: boolean
  onFileHover: () => void
}
const appNode = document.getElementById('app')

const CustomGroupDropWrapper = ({
  connectDropTarget,
  isOver,
  draggedItem,
  canDrop,
  group,
  children,
  onFileHover,
}: Props) => {
  const [wasOver, setWasOver] = useState(isOver)
  useEffect(() => {
    setWasOver(isOver)
  }, [isOver])

  const expand = () => {
    if (isOver && canDrop && wasOver && onFileHover) onFileHover()
  }

  useTimeout(expand, wasOver ? 1000 : null)
  const containerClass = cn({
    'is-over': isOver && canDrop,
    'is-track': draggedItem && isOver && draggedItem.track,
    'is-playlist': draggedItem && isOver && draggedItem.playlist,
    'is-group': draggedItem && isOver && draggedItem.group,
  })
  const isChannel =
    draggedItem && draggedItem.group && !draggedItem.group.parent
  const isReordering = isOver && isChannel && !group.parent
  const [firstChild, ...rest] = React.Children.toArray(children)
  const wrappedFirstChild = React.cloneElement(
    firstChild as React.ReactElement,
    {
      isOver: !isChannel && !isReordering && isOver && canDrop,
      isReordering,
    },
  )
  return connectDropTarget(
    <div className={containerClass}>
      {wrappedFirstChild}
      {rest}
      {isOver &&
        canDrop &&
        draggedItem?.files &&
        appNode &&
        ReactDOM.createPortal(<DropFilesIndicator />, appNode)}
    </div>,
  )
}

const dropWrapper = DropTarget(
  [TRACK, PLAYLIST, GROUP, FILE],
  target,
  collect,
)(CustomGroupDropWrapper)

const makeMapStateToProps = () => {
  const getGroupLocks = makeGetGroupLocks()
  const isFeatureEnabled = makeIsFeatureEnabled()
  return (state, { group }) => {
    const restrictions = getGroupLocks(state, {
      groupId: group.id,
    })
    return {
      ...restrictions,
      groups: getGroups(state),
      selectedItems: getAllSelectedItems(state),
      channelsCustomOrder: getChannelsCustomOrder(state),
      userSortedChannels: getUserSortedChannels(state),
      nestingEnabled: isFeatureEnabled(state, {
        featureName: TRACK_NESTING,
      }),
    }
  }
}

export default reduxConnect(makeMapStateToProps, {
  ...ExplorerActions,
  resetSelection,
  updateGroup,
  saveUserSortedChannels,
  uploadFilesToGroups,
})(dropWrapper) as any
